import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import {
  Badge,
  Card,
  Input,
  Button as ShButton,
} from '../../components/shadcn/index.js';
import { Button, FormField } from '../../components/ui/index.js';
import { useDirection, setLocale } from '../../hooks/useDirection.js';
import { platformApi } from '../../lib/platformApi.js';
import { usePlatformAuthStore, type PlatformAdmin } from '../../lib/platformAuthStore.js';

interface LoginResponse {
  tokens: { accessToken: string; expiresIn: number };
  platformAdmin: PlatformAdmin;
}

/**
 * Platform sign-in. Visually distinct from the tenant LoginPage — the
 * orange Platform pill is front-and-centre and the background uses a
 * subtle warning-tinted radial wash so support staff can always tell at
 * a glance which surface they're on (PRD §7).
 */
export function PlatformLoginPage(): JSX.Element {
  const { t } = useTranslation('platform');
  const { t: tc, i18n } = useTranslation();
  const dir = useDirection();
  const navigate = useNavigate();
  const isAr = i18n.language.startsWith('ar');
  // Subscribed selector so a sign-out in another tab re-renders this page.
  // Render-time redirect (not useEffect) so a signed-in admin landing
  // here never sees the login form flash before the navigation kicks in.
  const signedIn = usePlatformAuthStore((s) => !!s.accessToken);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (signedIn) return <Navigate to="/platform/tenants" replace />;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const r = await platformApi().post<LoginResponse>('/auth/login', { email, password });
      usePlatformAuthStore.getState().setSession({
        accessToken: r.data.tokens.accessToken,
        expiresIn: r.data.tokens.expiresIn,
        admin: r.data.platformAdmin,
      });
      navigate('/platform/tenants');
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(tc(`errors:${code ?? 'auth.unauthorized'}`, { defaultValue: tc('errors:server') }));
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div
      className="relative isolate flex min-h-screen flex-col bg-bg text-fg"
      dir={dir}
    >
      {/* Brand wash — warning-tinted radial so the page feels different
          from the tenant login the moment it loads. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_50%_0%,var(--color-warning)_0%,transparent_55%)] opacity-15"
      />

      <header className="flex items-center justify-between px-6 py-5 sm:px-10">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-warning text-fg-inverse">
            <ShieldCheck className="h-5 w-5" aria-hidden />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold uppercase tracking-wide">
              {t('header.title')}
            </div>
            <div className="text-xs text-fg-muted">{t('header.subtitle')}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <ShButton
            variant={isAr ? 'ghost' : 'outline'}
            size="sm"
            onClick={() => setLocale('en')}
            aria-pressed={!isAr}
            className="h-8 px-3 text-xs"
          >
            EN
          </ShButton>
          <ShButton
            variant={isAr ? 'outline' : 'ghost'}
            size="sm"
            onClick={() => setLocale('ar')}
            aria-pressed={isAr}
            className="h-8 px-3 text-xs"
          >
            AR
          </ShButton>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="mb-5 flex justify-center">
            <Badge
              variant="warning"
              className="px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.16em]"
            >
              {t('badge')}
            </Badge>
          </div>
          <Card className="p-7">
            <h1 className="text-2xl font-semibold">{t('login.title')}</h1>
            <p className="mt-1 text-sm text-fg-muted">{t('login.subtitle')}</p>
            <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
              <FormField id="platform-email" label={t('login.email')} required>
                <Input
                  id="platform-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </FormField>
              <FormField id="platform-password" label={t('login.password')} required>
                <Input
                  id="platform-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </FormField>
              {error && (
                <p
                  className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
                  role="alert"
                >
                  {error}
                </p>
              )}
              <Button type="submit" loading={loading}>
                {t('login.submit')}
              </Button>
            </form>
          </Card>
        </div>
      </main>

      <footer className="px-6 py-6 text-center text-xs text-fg-muted">
        © {year} · {t('header.elevated')}
      </footer>
    </div>
  );
}
